"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge/Badge";
import { Button } from "@/components/ui/button/Button";
import FormInput from "@/components/ui/form-input";
import SelectDropdown, { type SelectOption } from "@/components/ui/select-dropdown";
import { useCompanyDemoBarEnabled } from "./useCompanyDemoBarEnabled";
import {
  SALTUS_OS_DEMO_BAR_ACTIONS_CLASS,
  SALTUS_OS_DEMO_BAR_BUTTON_CLASS,
  SALTUS_OS_DEMO_BAR_CLASS,
  SALTUS_OS_DEMO_BAR_ROW_CLASS,
  SALTUS_OS_DEMO_BAR_SELECT_BUTTON_CLASS,
  SALTUS_OS_DEMO_BAR_SELECT_WRAPPER_CLASS,
} from "./saltusOnboardingDemoBarStyles";
import { OPENWORK_HOME_REFRESH_EVENT, scheduleDemoHomeRefreshPoll } from "./openworkDemoState";
import { agenticSurfaceKeys } from "@/components/agentic-surface/useAgenticSurface";
import { leadsKeys } from "@/services/api/agentic-v2/queries/keys";
import * as AgenticV2API from "@/services/api/agentic-v2";
import type { CompanyDemoPackItem, CompanyDemoPackActive } from "@/services/api/agentic-v2";
import {
  OnboardingIntakeDialog,
  type IntakeChannel,
} from "./OnboardingIntakeDialog";
import { formatDemoPackItemLabel } from "./formatDemoPackItemLabel";
import {
  FACTFIND_DEFAULTS_ACTION,
  FACTFIND_START_ACTION,
  isFactfindLeadAction,
  runFactfindDemoAction,
  storeFactfindDemoLeadId,
} from "./factfindDemoActions";
import { FactfindDefaultsDialog } from "./FactfindDefaultsDialog";
import { isJourneyMenuValue, journeyMenuRows, useDemoJourney } from "./useDemoJourney";
import { useHomeTaskConsoleTabs } from "@/components/agentic-surface/widgets/home/useHomeTaskConsoleTabs";

const SUCCESS_MESSAGE_VISIBLE_MS = 2_000;
const SUCCESS_MESSAGE_FADE_MS = 600;
const PREPARING_POLL_MS = 1_500;
/** Stop waiting for the worker to prepare a seeded task after this long. */
const PREPARING_TIMEOUT_MS = 90_000;

type MenuEntry = {
  value: string;
  itemId: string;
  kind: CompanyDemoPackItem["kind"];
  label: string;
  displayLabel: string;
  requirementCode?: string;
  description?: string;
  sectionId: string;
  sectionHeading: string;
};

const ONB01_INTAKE_REQUIREMENT_CODE = "ONB-01";
const ONB05_FACTFIND_REQUIREMENT_CODE = "ONB-05";
const FACTFIND_SECTION = "Factfind";

const FACTFIND_MENU_ENTRIES: {
  value: string;
  label: string;
  description: string;
}[] = [
  {
    value: FACTFIND_START_ACTION,
    label: "Start Factfind",
    description: "Create the Factfind on the launched ONB-05 lead (consent and route still apply).",
  },
  {
    value: "action:factfind-client-opens",
    label: "Client opens",
    description: "Drive a portal open on the launched ONB-05 lead.",
  },
  {
    value: "action:factfind-client-saves-partial",
    label: "Client saves partial",
    description: "Drive a partial portal save on the launched ONB-05 lead.",
  },
  {
    value: "action:factfind-client-submits",
    label: "Client submits",
    description: "Drive a full portal submit on the launched ONB-05 lead.",
  },
  {
    value: "action:factfind-client-submits-gaps",
    label: "Submits with gaps",
    description: "Drive a portal submit with gaps on the launched ONB-05 lead.",
  },
  {
    value: "action:factfind-client-submits-contradiction",
    label: "Submits with contradiction",
    description: "Drive a contradicting portal submit on the launched ONB-05 lead.",
  },
  {
    value: FACTFIND_DEFAULTS_ACTION,
    label: "Admin defaults",
    description: "Edit the company Factfind send-via-email rules (admin users only).",
  },
];

function factfindMenuEntries(): MenuEntry[] {
  return FACTFIND_MENU_ENTRIES.map((row) => ({
    value: row.value,
    itemId: row.value,
    kind: "action" as const,
    label: row.label,
    displayLabel: formatDemoPackItemLabel(row.label, ONB05_FACTFIND_REQUIREMENT_CODE),
    requirementCode: ONB05_FACTFIND_REQUIREMENT_CODE,
    description: row.description,
    sectionId: FACTFIND_SECTION,
    sectionHeading: FACTFIND_SECTION,
  }));
}

function menuEntryFromItem(item: CompanyDemoPackItem, sectionHeading: string): MenuEntry {
  const requirementCode = item.requirement_code?.trim() || undefined;
  return {
    value: item.id,
    itemId: item.id,
    kind: item.kind,
    label: item.label,
    displayLabel: formatDemoPackItemLabel(item.label, requirementCode),
    requirementCode,
    description: item.description,
    sectionId: sectionHeading,
    sectionHeading,
  };
}

const DEFAULT_CLEANUP_HINT =
  "Removes demo missions and tours created from this bar for your company. Standups and non-demo data are kept.";

const ACTION_VOICE_INTAKE = "action:voice-intake";
const ACTION_EMAIL_INTAKE = "action:email-intake";
const ACTION_TRANSCRIPT_INTAKE = "action:transcript-intake";
const ONBOARDING_LEADS_SECTION = "Onboarding leads";
const BOOKING_DEMO_SCENARIOS = new Set(["onb03-video-book", "onb03-adviser-books"]);
/** Demos that land in Leads → Awaiting contact, not Needs you. */
const LEADS_ONLY_SCENARIOS = new Set([
  "onb01-contact-overdue",
  "onb02-owner-override",
  "onb02-awaiting-bdm-contact",
  "onb02-unresponsive-warm",
  "onb02-meeting-cancelled",
]);
/** Demos that land on a lead outside Awaiting contact (Active / Lost pipeline). */
const LEAD_PIPELINE_SCENARIOS = new Set([
  "onb04-pack-received",
  "onb13-reinstated",
]);
/** Meeting-pack missions parked at an adviser gate — the reviewer continues in Needs you. */
const MEETING_PACK_GATE_SCENARIOS = new Set(["onb04-pack-choose", "onb04-pack-send"]);
/** ONB-02-AB gates for Abacus CGL / the Captain; the other Abacus scenarios land in an adviser's Lead offers. */
const ABACUS_NEEDS_YOU_SCENARIOS = new Set(["onb02ab-select-adviser", "onb02ab-all-rejected-captain"]);
const ABACUS_SCENARIO_PREFIX = "onb02ab-";
async function taskAppearsInNeedsYou(taskId: string): Promise<boolean> {
  const page = await AgenticV2API.listTasks("needs_me", { limit: 200 });
  return page.items.some((task) => task._id === taskId);
}

const INTAKE_ACTION_CHANNELS: Record<string, IntakeChannel> = {
  [ACTION_VOICE_INTAKE]: "voice",
  [ACTION_EMAIL_INTAKE]: "email",
  [ACTION_TRANSCRIPT_INTAKE]: "transcript",
};

function buildMenuEntries(pack: CompanyDemoPackActive | null): MenuEntry[] {
  if (!pack) return [];
  const bySection = new Map<string, CompanyDemoPackItem[]>();
  for (const item of pack.items) {
    const section = item.section || "Demos";
    const list = bySection.get(section) ?? [];
    list.push(item);
    bySection.set(section, list);
  }
  const entries: MenuEntry[] = journeyMenuRows(pack).map((row) => ({
    value: row.value,
    itemId: row.value,
    kind: "action",
    label: row.label,
    displayLabel: row.displayLabel,
    requirementCode: row.requirementCode,
    description: row.description,
    sectionId: row.section,
    sectionHeading: row.section,
  }));
  for (const [sectionHeading, items] of bySection) {
    for (const item of items) {
      entries.push(menuEntryFromItem(item, sectionHeading));
    }
  }

  if (pack.client_id === "saltus") {
    const intakeEntries: MenuEntry[] = [      {
        value: ACTION_VOICE_INTAKE,
        itemId: ACTION_VOICE_INTAKE,
        kind: "action",
        label: "Voice lead intake",
        displayLabel: formatDemoPackItemLabel("Voice lead intake", ONB01_INTAKE_REQUIREMENT_CODE),
        requirementCode: ONB01_INTAKE_REQUIREMENT_CODE,
        description: "Paste, upload, record, or link a Zoom meeting to start capture.",
        sectionId: ONBOARDING_LEADS_SECTION,
        sectionHeading: ONBOARDING_LEADS_SECTION,
      },
      {
        value: ACTION_EMAIL_INTAKE,
        itemId: ACTION_EMAIL_INTAKE,
        kind: "action",
        label: "Email lead intake",
        displayLabel: formatDemoPackItemLabel("Email lead intake", ONB01_INTAKE_REQUIREMENT_CODE),
        requirementCode: ONB01_INTAKE_REQUIREMENT_CODE,
        description: "Paste or upload a lead enquiry email to start capture.",
        sectionId: ONBOARDING_LEADS_SECTION,
        sectionHeading: ONBOARDING_LEADS_SECTION,
      },
      {
        value: ACTION_TRANSCRIPT_INTAKE,
        itemId: ACTION_TRANSCRIPT_INTAKE,
        kind: "action",
        label: "Transcript lead intake",
        displayLabel: formatDemoPackItemLabel("Transcript lead intake", ONB01_INTAKE_REQUIREMENT_CODE),
        requirementCode: ONB01_INTAKE_REQUIREMENT_CODE,
        description: "Paste or upload a call or video transcript to start capture.",
        sectionId: ONBOARDING_LEADS_SECTION,
        sectionHeading: ONBOARDING_LEADS_SECTION,
      },
    ];
    const onboardingIndex = entries.findIndex((entry) => entry.sectionId === ONBOARDING_LEADS_SECTION);
    const withIntake =
      onboardingIndex === -1
        ? [...intakeEntries, ...entries]
        : [
            ...entries.slice(0, onboardingIndex),
            ...intakeEntries,
            ...entries.slice(onboardingIndex),
          ];
    const actions = factfindMenuEntries();
    const lastFactfindIndex = withIntake.reduce(
      (last, entry, index) => (entry.sectionId === FACTFIND_SECTION ? index : last),
      -1,
    );
    return lastFactfindIndex === -1
      ? [...withIntake, ...actions]
      : [
          ...withIntake.slice(0, lastFactfindIndex + 1),
          ...actions,
          ...withIntake.slice(lastFactfindIndex + 1),
        ];
  }

  return entries;
}

function formatDemoError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === "string" && detail.trim()) {
      if (detail.includes("Could not resolve Progeny CRM")) {
        return "Progeny CRM not available for this replay — try Adrian & Deborah Dockey.";
      }
      if (detail.includes("not available offline")) {
        return "This replay needs cached CRM in samples/saturn_audio — try Dockey or re-pull the manifest.";
      }
      return detail;
    }
    if (error.response?.status === 403) return "Demo controls are not enabled for this user.";
    if (error.response?.status === 404) {
      return "Demo endpoint not found — try restarting the API container.";
    }
  }
  return error instanceof Error ? error.message : "Something went wrong";
}

export type CompanyDemoToolbarProps = {
  onChanged?: () => void;
  className?: string;
  /** When set, toolbar only shows on this home surface id. */
  surfaceId?: string | null;
};

export function CompanyDemoToolbar({ onChanged, className, surfaceId }: CompanyDemoToolbarProps) {
  const demoBarEnabled = useCompanyDemoBarEnabled();
  const { setActiveTab } = useHomeTaskConsoleTabs();
  const [pack, setPack] = useState<CompanyDemoPackActive | null>(null);
  const [selectedValue, setSelectedValue] = useState<string>("");
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [cleaningUp, setCleaningUp] = useState(false);
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [lastMessage, setLastMessage] = useState<string | null>(null);
  const [messageFading, setMessageFading] = useState(false);
  const messageFadeTimerRef = useRef<number | null>(null);
  const messageClearTimerRef = useRef<number | null>(null);
  const homePollCleanupRef = useRef<(() => void) | null>(null);
  const [scenarioQuery, setScenarioQuery] = useState("");
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [intakeChannel, setIntakeChannel] = useState<IntakeChannel>("voice");
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const [preparingTaskId, setPreparingTaskId] = useState<string | null>(null);

  const onAdvisorHome = surfaceId ? surfaceId === pack?.advisor_surface_id : true;
  const canUseDemoControls = demoBarEnabled && onAdvisorHome;
  const busy = Boolean(busyAction) || cleaningUp || Boolean(preparingTaskId);

  const menuEntries = useMemo(() => buildMenuEntries(pack), [pack]);
  const { runMenuAction: runJourneyMenuAction } = useDemoJourney(pack);

  const menuOptions = useMemo<SelectOption<string>[]>(
    () => menuEntries.map((entry) => ({ value: entry.value, label: entry.displayLabel })),
    [menuEntries],
  );

  const filteredMenuEntries = useMemo(() => {
    const needle = scenarioQuery.trim().toLowerCase();
    if (!needle) return menuEntries;
    return menuEntries.filter(
      (entry) =>
        entry.label.toLowerCase().includes(needle) ||
        entry.displayLabel.toLowerCase().includes(needle) ||
        entry.requirementCode?.toLowerCase().includes(needle) ||
        entry.description?.toLowerCase().includes(needle) ||
        entry.value.toLowerCase().includes(needle),
    );
  }, [menuEntries, scenarioQuery]);

  const filteredMenuOptions = useMemo(
    () => filteredMenuEntries.map((entry) => ({ value: entry.value, label: entry.displayLabel })),
    [filteredMenuEntries],
  );

  const selectedEntry =
    menuEntries.find((entry) => entry.value === selectedValue) ?? menuEntries[0];
  const selectedOption = selectedEntry
    ? { value: selectedEntry.value, label: selectedEntry.displayLabel }
    : undefined;

  const clearSuccessMessageTimers = useCallback(() => {
    if (messageFadeTimerRef.current) {
      window.clearTimeout(messageFadeTimerRef.current);
      messageFadeTimerRef.current = null;
    }
    if (messageClearTimerRef.current) {
      window.clearTimeout(messageClearTimerRef.current);
      messageClearTimerRef.current = null;
    }
  }, []);

  const showSuccessMessage = useCallback(
    (message: string) => {
      clearSuccessMessageTimers();
      setError(null);
      setMessageFading(false);
      setLastMessage(message);
      messageFadeTimerRef.current = window.setTimeout(() => setMessageFading(true), SUCCESS_MESSAGE_VISIBLE_MS);
      messageClearTimerRef.current = window.setTimeout(() => {
        setLastMessage(null);
        setMessageFading(false);
      }, SUCCESS_MESSAGE_VISIBLE_MS + SUCCESS_MESSAGE_FADE_MS);
    },
    [clearSuccessMessageTimers],
  );

  useEffect(
    () => () => {
      clearSuccessMessageTimers();
      homePollCleanupRef.current?.();
    },
    [clearSuccessMessageTimers],
  );

  useEffect(() => {
    if (!error) return;
    const timer = window.setTimeout(() => setError(null), 8_000);
    return () => window.clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!canUseDemoControls) return;
    setLoadingCatalog(true);
    void AgenticV2API.getActiveCompanyDemoPack()
      .then((active) => {
        setPack(active);
        setError(null);
        setSelectedValue((current) => {
          if (current && active.items.some((row) => row.id === current)) return current;
          return active.items[0]?.id ?? "";
        });
      })
      .catch((loadError) => setError(formatDemoError(loadError)))
      .finally(() => setLoadingCatalog(false));
  }, [canUseDemoControls]);

  const refreshHome = useCallback(() => {
    void queryClient.refetchQueries({ queryKey: agenticSurfaceKeys.all }, { cancelRefetch: false });
    // Leads-landing scenarios (e.g. ONB-13 reinstated) must refresh the Leads list too.
    void queryClient.invalidateQueries({ queryKey: leadsKeys.all });
    void queryClient.invalidateQueries({ queryKey: ["standup-briefing"] });
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(OPENWORK_HOME_REFRESH_EVENT));
    }
  }, [queryClient]);

  const scheduleHomeRefreshPoll = useCallback(() => {
    homePollCleanupRef.current?.();
    homePollCleanupRef.current = scheduleDemoHomeRefreshPoll(refreshHome);
  }, [refreshHome]);

  // Stay in "Preparing…" until the worker has finished AND the task row is actually rendered on Home.
  useEffect(() => {
    if (!preparingTaskId) return;
    let cancelled = false;
    const startedAt = Date.now();
    const finish = (message: string, ready: boolean) => {
      setPreparingTaskId(null);
      if (ready) setActiveTab("needs_you");
      showSuccessMessage(message);
    };
    const tick = async () => {
      while (!cancelled) {
        let preparing = true;
        try {
          ({ preparing } = await AgenticV2API.getSeededDemoTaskPreparing(preparingTaskId));
        } catch {
          return finish("Could not confirm the task — check Needs you", false);
        }
        if (!preparing) {
          await queryClient.refetchQueries({ queryKey: agenticSurfaceKeys.all }, { cancelRefetch: false });
          if (cancelled) return;
          if (await taskAppearsInNeedsYou(preparingTaskId)) return finish("Ready — open Needs you", true);
        }
        if (Date.now() - startedAt >= PREPARING_TIMEOUT_MS) return finish("Taking longer than expected — check Needs you", false);
        await new Promise((resolve) => window.setTimeout(resolve, PREPARING_POLL_MS));
      }
    };
    void tick();
    return () => {
      cancelled = true;
    };
  }, [preparingTaskId, queryClient, setActiveTab, showSuccessMessage]);

  const scenarioShowsInNeedsYou = useCallback((itemId: string) => {
    if (LEADS_ONLY_SCENARIOS.has(itemId)) return false;
    if (LEAD_PIPELINE_SCENARIOS.has(itemId)) return false;
    // The due revisit prompt is a live needs_review task; reinstate stays to do by hand.
    if (itemId === "onb13-record-lost") return true;
    if (itemId === "onb05-outstanding-mix") return false;
    if (BOOKING_DEMO_SCENARIOS.has(itemId)) return true;
    if (MEETING_PACK_GATE_SCENARIOS.has(itemId)) return true;
    if (itemId.startsWith(ABACUS_SCENARIO_PREFIX)) return ABACUS_NEEDS_YOU_SCENARIOS.has(itemId);
    return (
      itemId.startsWith("onb01-") ||
      itemId.startsWith("onb02-") ||
      itemId.startsWith("onb03-") ||
      itemId.startsWith("onb05-")
    );
  }, []);

  const handleRun = useCallback(async () => {
    const itemId = selectedOption?.value;
    if (!itemId) return;
    if (itemId in INTAKE_ACTION_CHANNELS) {
      setIntakeChannel(INTAKE_ACTION_CHANNELS[itemId]);
      setIntakeOpen(true);
      return;
    }
    if (itemId === FACTFIND_DEFAULTS_ACTION) {
      setDefaultsOpen(true);
      return;
    }
    if (isJourneyMenuValue(itemId)) {
      setBusyAction(itemId);
      clearSuccessMessageTimers();
      setError(null);
      try {
        showSuccessMessage(await runJourneyMenuAction(itemId));
        refreshHome();
      } catch (actionError) {
        setError(formatDemoError(actionError));
      } finally {
        setBusyAction(null);
      }
      return;
    }
    if (isFactfindLeadAction(itemId)) {
      setBusyAction(itemId);
      clearSuccessMessageTimers();
      setError(null);
      setLastMessage(null);
      setMessageFading(false);
      try {
        const outcome = await runFactfindDemoAction(itemId);
        showSuccessMessage(outcome.message);
        scheduleHomeRefreshPoll();
        onChanged?.();
      } catch (actionError) {
        setError(formatDemoError(actionError));
      } finally {
        setBusyAction(null);
      }
      return;
    }
    setBusyAction(itemId);
    clearSuccessMessageTimers();
    setError(null);
    setLastMessage(null);
    setMessageFading(false);
    try {
      const result = await AgenticV2API.seedActiveCompanyDemoPackItem(itemId);
      const factfindLeadId = (result as { fa_client_id?: unknown }).fa_client_id;
      if (itemId.startsWith("onb05-") && typeof factfindLeadId === "string" && factfindLeadId) {
        storeFactfindDemoLeadId(factfindLeadId);
      }
      if ((result as { reload?: boolean }).reload) {
        showSuccessMessage("Persona switched — reloading");
        window.setTimeout(() => window.location.reload(), 800);
        return;
      }
      const seededTaskId = (result as { task_id?: unknown }).task_id;
      if (typeof seededTaskId === "string") {
        if (scenarioShowsInNeedsYou(itemId)) setActiveTab("needs_you");
        setPreparingTaskId(seededTaskId);
        setLastMessage(`Preparing ${selectedEntry?.label ?? "task"}…`);
        scheduleHomeRefreshPoll();
        onChanged?.();
        return;
      }
      if (itemId === "action:standup") {
        await queryClient.invalidateQueries({ queryKey: ["standup-briefing"] });
        showSuccessMessage("Standup generated");
      } else if (selectedEntry?.kind === "scenario" && itemId.startsWith(ABACUS_SCENARIO_PREFIX) && !scenarioShowsInNeedsYou(itemId)) {
        showSuccessMessage("Launched — switch to the Abacus adviser persona to see Lead offers");
      } else if (itemId === "action:meetings" && (result as { skipped?: boolean }).skipped) {
        showSuccessMessage("Meetings demo already exists");
      } else if (selectedEntry?.kind === "scenario" && scenarioShowsInNeedsYou(itemId)) {
        setActiveTab("needs_you");
        showSuccessMessage("Launched — open Needs you");
      } else if (selectedEntry?.kind === "scenario" && LEADS_ONLY_SCENARIOS.has(itemId)) {
        showSuccessMessage("Launched — check Leads → Awaiting contact");
      } else if (selectedEntry?.kind === "scenario" && LEAD_PIPELINE_SCENARIOS.has(itemId)) {
        showSuccessMessage("Launched — check Leads");
      } else if (itemId === "onb05-outstanding-mix") {
        showSuccessMessage("Launched — check the Factfinds outstanding list");
      } else if (selectedEntry?.kind === "scenario") {
        showSuccessMessage("Launched");
      } else {
        showSuccessMessage("Done");
      }
      scheduleHomeRefreshPoll();
      onChanged?.();
    } catch (seedError) {
      setError(formatDemoError(seedError));
    } finally {
      setBusyAction(null);
    }
  }, [
    clearSuccessMessageTimers,
    onChanged,
    queryClient,
    refreshHome,
    runJourneyMenuAction,
    scheduleHomeRefreshPoll,
    scenarioShowsInNeedsYou,
    selectedEntry?.kind,
    selectedEntry?.label,
    selectedOption?.value,
    setActiveTab,
    showSuccessMessage,
  ]);

  const handleCleanup = useCallback(async () => {
    setCleaningUp(true);
    clearSuccessMessageTimers();
    setError(null);
    setLastMessage(null);
    setMessageFading(false);
    try {
      await AgenticV2API.cleanupActiveCompanyDemoPack();
      showSuccessMessage("Demo data cleared");
      refreshHome();
      onChanged?.();
    } catch (cleanupError) {
      setError(formatDemoError(cleanupError));
    } finally {
      setCleaningUp(false);
    }
  }, [clearSuccessMessageTimers, onChanged, refreshHome, showSuccessMessage]);

  if (!canUseDemoControls) return null;

  const statusMessage = lastMessage;
  const cleanupHint = pack?.cleanup_hint?.trim() || DEFAULT_CLEANUP_HINT;
  const runButtonLabel = preparingTaskId
    ? "Preparing…"
    : selectedEntry?.value && selectedEntry.value in INTAKE_ACTION_CHANNELS
      ? "Open"
      : selectedEntry?.kind === "scenario"
        ? "Launch"
        : "Run";
  const barTitle = error ?? statusMessage ?? undefined;
  const pickerValue = selectedOption?.value ?? "";

  return (
    <>
    <div
      className={clsx(SALTUS_OS_DEMO_BAR_CLASS, className)}
      data-testid="company-demo-toolbar"
      title={barTitle}
    >
      <div className={SALTUS_OS_DEMO_BAR_ROW_CLASS}>
        <Badge variant="chip" color="violet" size="sm" className="h-4 shrink-0 px-1 text-[10px] leading-none">
          Demo
        </Badge>
        <SelectDropdown
          inline
          className={SALTUS_OS_DEMO_BAR_SELECT_WRAPPER_CLASS}
          buttonClassName={SALTUS_OS_DEMO_BAR_SELECT_BUTTON_CLASS}
          options={filteredMenuOptions}
          value={pickerValue}
          onChange={setSelectedValue}
          disabled={busy || loadingCatalog || menuOptions.length === 0}
          usePortal
          dropdownClassName="max-w-xl"
          data-testid="company-demo-picker"
          renderSelectedItem={() =>
            loadingCatalog
              ? "Loading…"
              : (
                <span className="block min-w-0 truncate">
                  {selectedOption?.label ?? pack?.title ?? "Choose demo action…"}
                </span>
              )
          }
          renderListHeader={() => (
            <div className="mb-2 border-b border-slate-200 pb-2 dark:border-slate-600">
              <FormInput
                type="search"
                value={scenarioQuery}
                onChange={(event) => setScenarioQuery(event.target.value)}
                placeholder="Search demos…"
                data-testid="company-demo-picker-search"
              />
            </div>
          )}
          renderBeforeOption={(option, optionIndex) => {
            const entry = filteredMenuEntries[optionIndex];
            const previousSectionId =
              optionIndex > 0 ? filteredMenuEntries[optionIndex - 1]?.sectionId : null;
            if (!entry || entry.sectionId === previousSectionId) return null;

            return (
              <p
                className={clsx(
                  "px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400",
                  optionIndex === 0 ? "pt-1" : "pt-3",
                )}
                aria-hidden
              >
                {entry.sectionHeading}
              </p>
            );
          }}
          renderListItem={(option, _selected, focus) => {
            const entry = filteredMenuEntries.find((row) => row.value === option.value);

            return (
              <div className="flex w-full min-w-0 flex-col gap-1 px-3 py-2.5 pr-9">
                <div className="flex min-w-0 items-baseline gap-2">
                  {entry?.requirementCode ? (
                    <span
                      className={clsx(
                        "shrink-0 font-mono text-[10px] font-semibold uppercase tracking-wide",
                        focus ? "text-indigo-100" : "text-violet-600 dark:text-violet-300",
                      )}
                    >
                      {entry.requirementCode}
                    </span>
                  ) : null}
                  <span
                    className={clsx(
                      "min-w-0 truncate font-medium leading-snug",
                      focus ? "text-white" : "text-slate-900 dark:text-slate-100",
                    )}
                  >
                    {entry?.label ?? option.label}
                  </span>
                </div>
                {entry?.description ? (
                  <span
                    className={clsx(
                      "line-clamp-2 text-xs leading-relaxed",
                      focus ? "text-indigo-100" : "text-slate-500 dark:text-slate-300",
                    )}
                  >
                    {entry.description}
                  </span>
                ) : null}
              </div>
            );
          }}
        />
        {statusMessage ? (
          <span
            className={clsx(
              "hidden min-w-0 max-w-[10rem] flex-1 truncate text-[10px] leading-none text-emerald-700 transition-opacity duration-500 dark:text-emerald-400 xl:inline",
              messageFading && "opacity-0",
            )}
            data-testid="company-demo-message"
          >
            {statusMessage}
          </span>
        ) : (
          <span className="hidden min-w-0 flex-1 xl:inline" />
        )}
        <div className={SALTUS_OS_DEMO_BAR_ACTIONS_CLASS}>
          <Button
            size="sm"
            plain
            className={SALTUS_OS_DEMO_BAR_BUTTON_CLASS}
            loading={cleaningUp}
            disabled={busy}
            onClick={() => void handleCleanup()}
            data-testid="company-demo-cleanup"
            title={cleanupHint}
            aria-label={`Clear demo data. ${cleanupHint}`}
          >
            Clear
          </Button>
          <Button
            size="sm"
            color="indigo"
            className={SALTUS_OS_DEMO_BAR_BUTTON_CLASS}
            loading={Boolean(busyAction) || Boolean(preparingTaskId)}
            disabled={busy || loadingCatalog || !pickerValue}
            onClick={() => void handleRun()}
            data-testid="company-demo-run"
          >
            {runButtonLabel}
          </Button>
        </div>
      </div>
      <FactfindDefaultsDialog open={defaultsOpen} onClose={() => setDefaultsOpen(false)} />
    </div>
    <OnboardingIntakeDialog
      isOpen={intakeOpen}
      initialChannel={intakeChannel}
      onClose={() => setIntakeOpen(false)}
      onBridged={(result) => {
        showSuccessMessage(
          result.skipped ? "Existing intake mission reused" : "Intake started",
        );
        scheduleHomeRefreshPoll();
        onChanged?.();
      }}
    />
    </>
  );
}
